import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BalanceBillAttachment } from "@/model/balance-bill";
import {
  AlertCircle,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2,
} from "lucide-react";
import { QueryKeys } from "@/constants";
import { useAttachmentDetail } from "@/hooks/use-balance-bill";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { fetchSingleBalanceBillAttachment } from "@/services/balance-bill-service";
import useUserInfo from "@/hooks/use-user-info";
import PdfViewer from "../base64-pdf-viewer/base64-pdf-viewer";
import { PdfFallbackNotice } from "../pdf-fallback-notice";
import { selectPdfViewerMode } from "@/lib/pdf-viewer-support";

type ImagePreviewCarouselProps = {
  attachments: BalanceBillAttachment[] | undefined;
  ticketId: string | undefined;
};

function SingleBillImage({
  attachmentId,
  ticketId,
}: {
  attachmentId: string;
  ticketId: string;
}) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [{ mode: pdfViewerMode }] = useState(selectPdfViewerMode);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);

  const { data, isLoading, isError } = useAttachmentDetail({
    attachmentId,
    ticketId,
  });

  useEffect(() => {
    let objectUrl: string | null = null;

    try {
      if (data?.blob) {
        if (!(data.blob instanceof Blob)) {
          console.error("Expected Blob but got:", typeof data.blob, data.blob);
          setError("Invalid file format");
          return;
        }

        objectUrl = URL.createObjectURL(data.blob);
        setFileUrl(objectUrl);
        setError(null);
      }
    } catch (err) {
      console.error("Error creating object URL:", err);
      setError("Failed to load file");
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [data]);

  /*
   * Read the attachment into memory for the app viewer. Only needed when the
   * browser cannot use its own plugin: framing a blob: URL is refused by the
   * deployed frame-src on those browsers. The Blob is already in hand, so
   * nothing is fetched and connect-src never applies. See CSRTB-1034.
   */
  useEffect(() => {
    const blob = data?.blob;
    if (pdfViewerMode !== "app" || !(blob instanceof Blob)) return;
    if (data?.contentType !== "application/pdf") return;

    let isStale = false;
    blob
      .arrayBuffer()
      .then((buffer) => {
        if (!isStale) setPdfBytes(new Uint8Array(buffer));
      })
      .catch(() => {
        if (!isStale) setError("Failed to load file");
      });

    return () => {
      isStale = true;
    };
  }, [pdfViewerMode, data?.blob, data?.contentType]);

  if (isLoading) {
    return (
      <div className="flex h-[500px] w-full items-center justify-center border border-border bg-muted">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="size-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (isError || error) {
    return (
      <div className="flex h-[500px] w-full items-center justify-center border border-border bg-muted">
        <div className="flex flex-col items-center gap-2">
          <AlertCircle className="size-8 text-red-500" />
          <span className="text-sm text-red-600">
            {error || "Failed to load file"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[500px] w-full items-center justify-center border border-border bg-muted">
      {data?.contentType === "application/pdf" ? (
        pdfViewerMode === "native" ? (
          <iframe
            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            className="size-full border-0"
            title="PDF Preview"
          />
        ) : (
          <div className="size-full overflow-auto bg-white">
            {fileUrl ? (
              <PdfFallbackNotice
                downloadUrl={fileUrl}
                downloadFileName="bill-attachment.pdf"
                compact
              />
            ) : null}
            {pdfBytes ? <PdfViewer data={pdfBytes} maxWidth={1000} /> : null}
          </div>
        )
      ) : (
        fileUrl && (
          <img
            src={fileUrl}
            alt="Bill attachment"
            className="max-h-full max-w-full object-contain"
            onError={() => setError("Image failed to render")}
          />
        )
      )}
    </div>
  );
}

export default function ImagePreviewCarousel({
  attachments,
  ticketId,
}: ImagePreviewCarouselProps) {
  const userInfo = useUserInfo();
  const queryClient = useQueryClient();

  const [index, setIndex] = useState(0);
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const buildAttachmentRequest = useCallback(
    (attachmentId: string) => ({
      groupId: userInfo?.groupId,
      divisionId: userInfo?.divisionId,
      planId: activeCoverage?.planId,
      claimCoverageCode: activeCoverage?.claimCoverageCode,
      memberId: userInfo?.memberId,
      attachmentId,
      ticketId,
      token: userInfo?.accessToken,
    }),
    [userInfo, activeCoverage, ticketId],
  );

  const prefetchAttachment = useCallback(
    (attachmentId: string) => {
      queryClient.prefetchQuery({
        queryKey: [
          QueryKeys.BALANCE_BILL_SINGLE_ATTACHMENT,
          attachmentId,
          activeCoverage?.claimCoverageCode,
          activeCoverage?.planId,
        ],
        queryFn: () =>
          fetchSingleBalanceBillAttachment(
            buildAttachmentRequest(attachmentId),
          ),
        staleTime: 5 * 60 * 1000,
      });
    },
    [queryClient, buildAttachmentRequest, activeCoverage],
  );

  useEffect(() => {
    if (!attachments?.length || attachments.length <= 1) return;

    const nextIndex = (index + 1) % attachments.length;
    const prevIndex = (index - 1 + attachments.length) % attachments.length;

    prefetchAttachment(attachments[nextIndex].attachmentId);

    if (attachments.length > 2) {
      prefetchAttachment(attachments[prevIndex].attachmentId);
    }
  }, [index, attachments, prefetchAttachment]);

  if (!attachments?.length || !ticketId) {
    return <p>No images available</p>;
  }

  const next = () => setIndex((i) => (i + 1) % attachments.length);
  const prev = () =>
    setIndex((i) => (i - 1 + attachments.length) % attachments.length);

  const currentAttachment = attachments[index];

  return (
    <div className="w-full">
      {/* Only render the current image */}
      <SingleBillImage
        key={currentAttachment.attachmentId}
        attachmentId={currentAttachment.attachmentId}
        ticketId={ticketId}
      />

      <div className="mt-3 flex w-full items-center justify-center gap-4">
        <button
          title="Previous"
          type="button"
          onClick={prev}
          disabled={attachments.length <= 1}
          className="rounded p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeftIcon className="size-5" />
        </button>
        <span className="text-sm font-medium">
          {index + 1} of {attachments.length}
        </span>
        <button
          title="Next"
          type="button"
          onClick={next}
          disabled={attachments.length <= 1}
          className="rounded p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRightIcon className="size-5" />
        </button>
      </div>
    </div>
  );
}
